import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

export class ContainerCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Layout patterns
    this.commandPatterns.set(
      /(?:set |change )?layout (?:to )?(flex|grid|block|inline|inline-flex|inline-grid)/i,
      (match) => ({ style: { display: match[1].toLowerCase() } })
    );

    // Flex direction patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:flex[\s-]?)?direction (?:to )?(row|column|row-reverse|column-reverse)/i,
      (match) => ({ style: { flexDirection: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /(?:arrange |stack )?(horizontal(?:ly)?|vertical(?:ly)?)/i,
      (match) => {
        const isHorizontal = match[1].toLowerCase().startsWith("horizontal");
        return { style: { display: "flex", flexDirection: isHorizontal ? "row" : "column" } };
      }
    );

    // Justify content patterns
    this.commandPatterns.set(
      /(?:justify |align )(?:content )?(?:to )?(start|end|center|space-between|space-around|space-evenly)/i,
      (match) => ({ style: { justifyContent: match[1].toLowerCase().replace("-", " ") } })
    );

    // Align items patterns
    this.commandPatterns.set(
      /align[\s-]?items? (?:to )?(start|end|center|stretch|baseline)/i,
      (match) => ({ style: { alignItems: match[1].toLowerCase() } })
    );

    // Center content patterns
    this.commandPatterns.set(
      /center (?:all )?(?:content|children|items)/i,
      () => ({ style: { display: "flex", justifyContent: "center", alignItems: "center" } })
    );

    // Gap patterns
    this.commandPatterns.set(
      /(?:set )?gap (?:to )?(\d+)(?:px)?/i,
      (match) => ({ style: { gap: `${match[1]}px` } })
    );

    // Padding patterns
    this.commandPatterns.set(
      /(?:add |set )?padding (?:to )?(\d+)(?:px)?/i,
      (match) => ({ style: { padding: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /(?:add |set )?(top|bottom|left|right)[\s-]?padding (?:to )?(\d+)(?:px)?/i,
      (match) => {
        const side = match[1].toLowerCase();
        const paddingKey = `padding${side.charAt(0).toUpperCase() + side.slice(1)}`;
        return { style: { [paddingKey]: `${match[2]}px` } };
      }
    );

    this.commandPatterns.set(
      /remove padding/i,
      () => ({ style: { padding: "0" } })
    );

    // Margin patterns
    this.commandPatterns.set(
      /(?:add |set )?margin (?:to )?(\d+)(?:px)?/i,
      (match) => ({ style: { margin: `${match[1]}px` } })
    );

    this.commandPatterns.set(
      /(?:add |set )?(top|bottom|left|right)[\s-]?margin (?:to )?(\d+)(?:px)?/i,
      (match) => {
        const side = match[1].toLowerCase();
        const marginKey = `margin${side.charAt(0).toUpperCase() + side.slice(1)}`;
        return { style: { [marginKey]: `${match[2]}px` } };
      }
    );

    // Border patterns
    this.commandPatterns.set(
      /(?:add |set )?border (?:width )?(\d+)(?:px)?/i,
      (match) => ({ style: { borderWidth: `${match[1]}px`, borderStyle: "solid" } })
    );

    this.commandPatterns.set(
      /(?:set )?border[\s-]?style (?:to )?(solid|dashed|dotted|double|none)/i,
      (match) => ({ style: { borderStyle: match[1].toLowerCase() } })
    );

    this.commandPatterns.set(
      /remove border/i,
      () => ({ style: { border: "none" } })
    );

    // Border radius patterns
    this.commandPatterns.set(
      /(?:make |set )?(rounded|square|circular)/i,
      (match) => {
        const style = match[1].toLowerCase();
        const radiusMap: Record<string, string> = {
          rounded: "8px",
          square: "0px",
          circular: "50%"
        };
        return { style: { borderRadius: radiusMap[style] } };
      }
    );

    this.commandPatterns.set(
      /(?:set )?(?:border[\s-]?)?radius (?:to )?(\d+)(?:px)?/i,
      (match) => ({ style: { borderRadius: `${match[1]}px` } })
    );

    // Background patterns
    this.commandPatterns.set(
      /(?:set |change )?background (?:color )?(?:to )?([#\w]+)/i,
      (match) => {
        const color = this.parseColorValue(match[1]);
        return color ? { style: { backgroundColor: color } } : null;
      }
    );

    this.commandPatterns.set(
      /remove background/i,
      () => ({ style: { backgroundColor: "transparent" } })
    );

    // Shadow patterns
    this.commandPatterns.set(
      /(?:add |set )?(shadow|elevation)/i,
      () => ({ style: { boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" } })
    );

    this.commandPatterns.set(
      /remove shadow/i,
      () => ({ style: { boxShadow: "none" } })
    );

    // Overflow patterns
    this.commandPatterns.set(
      /(?:set )?overflow (?:to )?(hidden|visible|scroll|auto)/i,
      (match) => ({ style: { overflow: match[1].toLowerCase() } })
    );

    // Grid patterns
    this.commandPatterns.set(
      /(?:set )?grid[\s-]?columns? (?:to )?(\d+)/i,
      (match) => ({ style: { display: "grid", gridTemplateColumns: `repeat(${match[1]}, 1fr)` } })
    );

    this.commandPatterns.set(
      /(?:set )?grid[\s-]?rows? (?:to )?(\d+)/i,
      (match) => ({ style: { display: "grid", gridTemplateRows: `repeat(${match[1]}, 1fr)` } })
    );

    // Wrap patterns
    this.commandPatterns.set(
      /(?:enable |disable )?(?:flex[\s-]?)?wrap/i,
      (match) => ({ style: { flexWrap: match[0].includes("disable") ? "nowrap" : "wrap" } })
    );

    // Position patterns
    this.commandPatterns.set(
      /(?:set )?position (?:to )?(relative|absolute|fixed|sticky|static)/i,
      (match) => ({ style: { position: match[1].toLowerCase() } })
    );

    // Z-index patterns
    this.commandPatterns.set(
      /(?:set )?z[\s-]?index (?:to )?(\d+)/i,
      (match) => ({ style: { zIndex: match[1] } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.CONTAINER && this.hasContainerKeywords(command);
  }

  private hasContainerKeywords(command: string): boolean {
    const containerKeywords = [
      "container", "box", "div", "section", "wrapper",
      "layout", "flex", "grid", "block",
      "direction", "horizontal", "vertical", "stack",
      "justify", "align", "center",
      "padding", "margin", "gap", "spacing",
      "border", "rounded", "radius", "shadow",
      "background", "overflow", "wrap",
      "position", "z-index", "elevation"
    ];
    const lowerCommand = command.toLowerCase();
    return containerKeywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors
      const colorValue = this.parseColorValue(part);
      if (colorValue) {
        const lowerPart = part.toLowerCase();
        if (lowerPart.includes("background") || lowerPart.includes("bg")) {
          changes.style = { ...changes.style, backgroundColor: colorValue };
        } else if (lowerPart.includes("border")) {
          changes.style = { ...changes.style, borderColor: colorValue };
        }
      }

      // Parse size values - IMPORTANT: Use 'size' not 'style' for dimensions
      const lowerPart = part.toLowerCase();
      if (lowerPart.includes("width") && !lowerPart.includes("border") && !lowerPart.includes("min") && !lowerPart.includes("max")) {
        const widthValue = this.parseNumericValue(part);
        if (widthValue) {
          changes.size = { ...changes.size, width: widthValue };
        }
      }

      if (lowerPart.includes("height") && !lowerPart.includes("min") && !lowerPart.includes("max")) {
        const heightValue = this.parseNumericValue(part);
        if (heightValue) {
          changes.size = { ...changes.size, height: heightValue };
        }
      }

      // Parse min/max dimensions
      if (lowerPart.includes("min-width") || lowerPart.includes("min width")) {
        const value = this.parseNumericValue(part);
        if (value) {
          changes.style = { ...changes.style, minWidth: value };
        }
      }

      if (lowerPart.includes("max-width") || lowerPart.includes("max width")) {
        const value = this.parseNumericValue(part);
        if (value) {
          changes.style = { ...changes.style, maxWidth: value };
        }
      }

      if (lowerPart.includes("min-height") || lowerPart.includes("min height")) {
        const value = this.parseNumericValue(part);
        if (value) {
          changes.style = { ...changes.style, minHeight: value };
        }
      }

      if (lowerPart.includes("max-height") || lowerPart.includes("max height")) {
        const value = this.parseNumericValue(part);
        if (value) {
          changes.style = { ...changes.style, maxHeight: value };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the container command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      'Set layout to flex',
      'Make grid layout',
      'Arrange horizontally',
      'Stack vertically',
      'Center all content',
      'Justify content center',
      'Align items start',
      'Set gap to 16px',
      'Add padding 20px',
      'Set top padding to 10px',
      'Add margin 15px',
      'Add border 2px',
      'Set border style to dashed',
      'Make rounded corners',
      'Set border radius to 12px',
      'Set background color to #f0f0f0',
      'Add shadow',
      'Set overflow to hidden',
      'Set grid columns to 3',
      'Enable flex wrap',
      'Set position to relative',
      'Set z-index to 10',
      'Make flex with gap 20px and center content',
      'Add padding 16px and rounded corners'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Layout suggestions
    const currentDisplay = component.style?.display || "block";
    if (currentDisplay !== "flex") {
      suggestions.push("Set layout to flex", "Make flexbox container");
    }
    if (currentDisplay !== "grid") {
      suggestions.push("Make grid layout", "Set layout to grid");
    }

    // Direction suggestions (if flex)
    if (currentDisplay === "flex") {
      const currentDirection = component.style?.flexDirection || "row";
      if (currentDirection !== "column") {
        suggestions.push("Stack vertically", "Set direction to column");
      }
      if (currentDirection !== "row") {
        suggestions.push("Arrange horizontally", "Set direction to row");
      }
      suggestions.push("Center all content", "Enable flex wrap");
    }

    // Grid suggestions (if grid)
    if (currentDisplay === "grid") {
      suggestions.push("Set grid columns to 3", "Set grid rows to 2");
    }

    // Spacing suggestions
    if (!component.style?.padding) {
      suggestions.push("Add padding", "Add padding 16px");
    } else {
      suggestions.push("Remove padding", "Increase padding");
    }

    if (!component.style?.gap && (currentDisplay === "flex" || currentDisplay === "grid")) {
      suggestions.push("Set gap to 16px", "Add spacing between items");
    }

    if (!component.style?.margin) {
      suggestions.push("Add margin");
    }

    // Border suggestions
    if (!component.style?.border && !component.style?.borderWidth) {
      suggestions.push("Add border", "Add border 2px");
    } else {
      suggestions.push("Remove border", "Make border dashed");
    }

    // Style suggestions
    if (!component.style?.borderRadius) {
      suggestions.push("Make rounded corners", "Set border radius to 8px");
    }

    if (!component.style?.boxShadow || component.style.boxShadow === "none") {
      suggestions.push("Add shadow effect");
    } else {
      suggestions.push("Remove shadow");
    }

    if (!component.style?.backgroundColor || component.style.backgroundColor === "transparent") {
      suggestions.push("Set background color");
    }

    return suggestions.slice(0, 10);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const style = changes.style;

    // Validate display values
    if (typeof style?.display === "string") {
      const validDisplays = ["flex", "grid", "block", "inline", "inline-flex", "inline-grid", "none"];
      if (!validDisplays.includes(style.display)) {
        return { valid: false, error: `Invalid display value: ${style.display}` };
      }
    }

    // Validate flex direction
    if (typeof style?.flexDirection === "string") {
      const validDirections = ["row", "column", "row-reverse", "column-reverse"];
      if (!validDirections.includes(style.flexDirection)) {
        return { valid: false, error: `Invalid flex direction: ${style.flexDirection}` };
      }
    }

    // Validate justify content
    if (typeof style?.justifyContent === "string") {
      const validValues = ["start", "end", "center", "space-between", "space-around", "space-evenly", "flex-start", "flex-end"];
      const value = style.justifyContent.replace(" ", "-");
      if (!validValues.some(v => v === value || v === style.justifyContent)) {
        return { valid: false, error: `Invalid justify content: ${style.justifyContent}` };
      }
    }

    // Validate align items
    if (typeof style?.alignItems === "string") {
      const validValues = ["start", "end", "center", "stretch", "baseline", "flex-start", "flex-end"];
      if (!validValues.includes(style.alignItems)) {
        return { valid: false, error: `Invalid align items: ${style.alignItems}` };
      }
    }

    // Validate overflow
    if (typeof style?.overflow === "string") {
      const validValues = ["visible", "hidden", "scroll", "auto"];
      if (!validValues.includes(style.overflow)) {
        return { valid: false, error: `Invalid overflow value: ${style.overflow}` };
      }
    }

    // Validate position
    if (typeof style?.position === "string") {
      const validValues = ["static", "relative", "absolute", "fixed", "sticky"];
      if (!validValues.includes(style.position)) {
        return { valid: false, error: `Invalid position value: ${style.position}` };
      }
    }

    // Validate z-index
    if (style?.zIndex) {
      const zIndex = parseInt(String(style.zIndex), 10);
      if (isNaN(zIndex)) {
        return { valid: false, error: `Invalid z-index value: ${style.zIndex}` };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const containerCommandHandler = new ContainerCommandHandler();
